/**
 * Runtime template resolver for org-specific branding.
 *
 * Takes scenario objects (email or device) and replaces generic "company"
 * references with org-specific values. Handles both LEGITIMATE emails
 * (use real org domain) and PHISHING emails (use fake lookalike domains).
 *
 * The replacement is applied to ALL string fields recursively, so any
 * new field added to scenarios automatically gets resolved.
 */

import { OrgConfig } from '../data/orgConfigs';

/**
 * Build the replacement map from an org config.
 * Order matters — more specific patterns must come before less specific ones.
 */
function buildReplacements(config: OrgConfig): Array<[RegExp, string]> {
  return [
    // Specific subdomains first (before the generic company.com catch-all)
    [/sharepoint\.company\.com/gi, config.sharepointDomain],
    [/intranet\.company\.com/gi, config.intranetDomain],
    [/training\.company\.com/gi, config.trainingDomain],
    [/vpn\.company\.com/gi, config.vpnDomain],
    [/id\.company\.com/gi, `id.${config.emailDomain}`],
    [/benefits\.company\.com/gi, `hr.${config.emailDomain}`],
    [/hr\.company\.com/gi, `hr.${config.emailDomain}`],
    [/software\.company\.com/gi, `software.${config.emailDomain}`],
    [/sccm\.company\.com/gi, `sccm.${config.emailDomain}`],
    [/admin\.company\.com/gi, `admin.${config.emailDomain}`],
    [/expenses\.company\.com/gi, `expenses.${config.emailDomain}`],

    // Fake phishing lookalike domains (company-X.TLD → org-specific fakes)
    [/company-it\.org/gi, config.fakeDomains.it],
    [/company-hr\.net/gi, config.fakeDomains.hr],
    [/company-invoicing\.com/gi, config.fakeDomains.finance],
    [/company-finance\.org/gi, config.fakeDomains.financeOrg],
    [/company-surveys\.com/gi, config.fakeDomains.surveys],
    [/company-notifications\.net/gi, config.fakeDomains.notifications],
    [/company-security\.com/gi, config.fakeDomains.security],
    [/company-exec\.net/gi, config.fakeDomains.exec],
    [/company-learn\.org/gi, config.fakeDomains.learn],
    [/company-mail\.net/gi, config.fakeDomains.mail],
    [/company-support\.net/gi, config.fakeDomains.support],
    [/accounting-company\.com/gi, config.fakeDomains.finance],

    // Generic company.com catch-all (must be AFTER specific subdomains)
    [/company\.com/gi, config.emailDomain],

    // WiFi network names
    [/CompanyWiFi[-_]Secure/gi, config.wifiNetwork],
    [/CompanyWiFi/gi, config.wifiNetwork],

    // Department names (case-sensitive where needed)
    [/\bIT Support\b/g, config.itDepartment],
    [/\bIT Department\b/g, config.itDepartmentFull],
    [/\bCompany IT Department\b/gi, config.itDepartmentFull],
    [/\bCompany IT\b/gi, config.itDepartment],

    // Executive titles
    [/\bCEO\b/g, config.executiveTitle],
    [/\bCEO's Office\b/gi, config.executiveOffice],
    [/\bExecutive Team\b/gi, config.executiveOffice],
    [/\bChancellor\b/g, config.executiveTitle],
    [/\bChancellor's Office\b/gi, config.executiveOffice],

    // Organization name in running text
    [/\bCompany Inc\.?\b/gi, config.orgName],
    [/\bCompany LLC\b/gi, config.orgName],
    [/\bthe company\b/gi, config.orgShortName],

    // Learning & Development department
    [/\bLearning & Development\b/gi, `${config.hrDepartment}`],
    [/\bLearning and Development\b/gi, `${config.hrDepartment}`],

    // Location references
    [/\bBoston, MA\b/gi, config.location],
    [/\bBoston\b/g, config.location.split(',')[0]],
    [/\bin Chicago\b/gi, `in ${config.location.split(',')[0]}`],
    [/\bChicago\b/g, config.location.split(',')[0]],
    [/\bHilton Chicago\b/gi, `hotel in ${config.location.split(',')[0]}`],

    // Generic "company" references in running text
    [/\bcompany laptop\b/gi, `${config.orgShortName} laptop`],
    [/\bcompany network\b/gi, `${config.orgShortName} network`],
    [/\bcompany policy\b/gi, `${config.orgShortName} policy`],
    [/\bcompany systems\b/gi, `${config.orgShortName} systems`],
    [/\bcompany VPN\b/gi, `${config.orgShortName} VPN`],
    [/\bCompany mandated\b/gi, `${config.orgShortName} mandated`],
    [/\bcompany uses\b/gi, `${config.orgShortName} uses`],
    [/\byour company\b/gi, `${config.orgShortName}`],
    [/\bcompany's\b/gi, `${config.orgShortName}'s`],

    // Resolve any remaining standalone template noun. Capitalized "Company"
    // commonly prefixes a proper internal system/team name; lowercase "company"
    // is usually ordinary running text and should remain organization-neutral.
    [/\bCompany\b/g, config.orgShortName],
    [/\bcompany\b/g, 'organization'],

    // MFA system
    [/\bGoogle Authenticator\b/g, config.mfaApp],
    [/\bOkta Verify\b/g, config.mfaApp],
  ];
}

/**
 * Apply replacements to a single string.
 */
function resolveString(text: string, replacements: Array<[RegExp, string]>): string {
  let result = text;
  for (const [pattern, replacement] of replacements) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

/**
 * Recursively apply org template resolution to all string fields in an object.
 * Returns a new object (does not mutate the original).
 */
export function resolveOrgTemplates<T>(obj: T, config: OrgConfig): T {
  if (obj == null) return obj;

  const replacements = buildReplacements(config);

  function resolve(value: any): any {
    if (typeof value === 'string') {
      return resolveString(value, replacements);
    }
    if (Array.isArray(value)) {
      return value.map(resolve);
    }
    if (typeof value === 'object' && value !== null) {
      const result: any = {};
      for (const key of Object.keys(value)) {
        result[key] = resolve(value[key]);
      }
      return result;
    }
    return value;
  }

  return resolve(obj);
}

/**
 * Resolve the trustedDomains array for an org config.
 * These are the domains that should be treated as "legitimate" in the experiment.
 */
export function getOrgTrustedDomains(config: OrgConfig): string[] {
  return [config.emailDomain, ...config.internalDomains];
}
